// ✅ src/pages/Sales/EMIPaymentEntry.jsx
// FULL FILE - API INTEGRATED + ACTIVE-ONLY UPDATE MODAL + PRINT PER ROW + LOCALSTORAGE HEADERS ADDED

import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import SalesLayoutWrapper from "../../components/SalesLayoutWrapper";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaArrowLeft, FaSave, FaTimes, FaPrint } from "react-icons/fa";

const BASE_URL = import.meta?.env?.VITE_BASE_URL || "https://nissi-india.com";

const EMIPaymentEntry = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const navigate = useNavigate();
  const location = useLocation();

  // ✅ localStorage helper
  const getLS = (key, fallback = "") => {
    if (typeof window === "undefined") return fallback;
    const val = localStorage.getItem(key);
    return val !== null && val !== undefined ? val : fallback;
  };

  // ✅ localStorage values
  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchId = getLS("branchId") || getLS("branchid");
  const branch = getLS("branchType") || getLS("branchtype");
  const userId = getLS("userId") || getLS("loginuserid") || getLS("loginUserId");

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  const loginUserId = String(
    getLS("loginuserid") || getLS("userId") || getLS("loginUserId") || "0"
  );
  const defaultLocation = String(getLS("location") || "HEADOFFICE");

  // -------------------------
  // Dropdowns + Master states
  // -------------------------
  const [customers, setCustomers] = useState([]);
  const [assets, setAssets] = useState([]);

  // Header form (read-only)
  const [formData, setFormData] = useState({
    customerId: "",
    customerName: "",
    leadSource: "",
    productName: "",
    salesPerson: "",
    fullAddress: "",
    mobileNumber: "",
    totalSqFeet: "",
    plotNoAssetCode: "",
    assetId: "",
    assetClass: "",
    location: defaultLocation,
    address: "",
    propertyInNameOf: "",
    dateOfAcquisition: "",
    valueOfAsset: "",
    purchaseValue: "",
    enquiryNumber: "",
  });

  // Payment grid
  const [gridRows, setGridRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // -------------------------
  // Update Modal (ACTIVE only)
  // -------------------------
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [activeRow, setActiveRow] = useState(null);

  const [updateData, setUpdateData] = useState({
    date: "",
    customerIdName: "",
    dueAmount: "",
    monthYear: "",
    status: "Paid",
    amountType: "Cash",
  });

  // -------------------------
  // Utils
  // -------------------------
  const formatAmount = (amount) => {
    const n = Number(amount || 0);
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(n);
  };

  const isActiveStatus = (s) => {
    const v = String(s || "").trim().toLowerCase();
    return v === "active" || v === "pending";
  };

  const gridMapped = useMemo(() => {
    return (gridRows || []).map((r, idx) => ({
      slNo: idx + 1,
      emitype: r.emitype,
      emidetid: r.fld_emidetid,
      customerid: r.fld_customerid,
      date: r.fld_acquisitiondate,
      billno: r.fld_billno,
      phase: r.fld_remarks,
      payableAmount: r.fld_assetvalue,
      paidAmount: r.fld_payingamount,
      balanceAmount: r.fld_balanceamount,
      overDuePlusInterest:
        r.fld_overdueamount || r.fld_overdue_interest || r.fld_overdue || "",
      paymentMode: r.fld_paymentmode,
      status: r.fld_status,
      canUpdate:
        isActiveStatus(r.fld_status) &&
        String(r.emitype || "").toUpperCase() === "EMIDUE",
    }));
  }, [gridRows]);

  // -------------------------
  // API Calls
  // -------------------------
  const fetchCustomers = async () => {
    const { data } = await axios.get(`${BASE_URL}/api/emiENtry/customers`, {
      params: { type: "EMI" },
      headers: commonHeaders,
    });
    if (!data?.success) throw new Error(data?.message || "Failed to load customers");
    setCustomers(data.data || []);
  };

  const fetchAssets = async () => {
    const { data } = await axios.get(`${BASE_URL}/api/emiENtry/assets`, {
      headers: commonHeaders,
    });
    if (!data?.success) throw new Error(data?.message || "Failed to load assets");
    setAssets(data.data || []);
  };

  const fetchCustomerDetails = async (customerId) => {
    const { data } = await axios.get(`${BASE_URL}/api/emiENtry/customerDetails`, {
      params: { customerId },
      headers: commonHeaders,
    });
    if (!data?.success) throw new Error(data?.message || "Failed to load customer details");
    return data.data?.[0] || null;
  };

  const fetchPaymentGrid = async (custid) => {
    const { data } = await axios.get(`${BASE_URL}/api/emiENtry/paymentGrid`, {
      params: { custid },
      headers: commonHeaders,
    });
    if (!data?.success) throw new Error(data?.message || "Failed to load payment grid");
    setGridRows(data.data || []);
  };

  const fetchDueDetails = async (emidetid) => {
    const { data } = await axios.get(`${BASE_URL}/api/emiENtry/dueDetails`, {
      params: { emidetid },
      headers: commonHeaders,
    });
    if (!data?.success) throw new Error(data?.message || "Failed to load due details");
    return data.data?.[0] || null;
  };

  const fetchEmiBillNo = async () => {
    const { data } = await axios.get(`${BASE_URL}/api/emiENtry/billno/emi`, {
      headers: commonHeaders,
    });
    if (!data?.success) throw new Error(data?.message || "Failed to get EMI bill no");
    return data.billno;
  };

  const markDuePaid = async ({ emidetid, billno, paidby }) => {
    const { data } = await axios.put(
      `${BASE_URL}/api/emiENtry/markDuePaid`,
      {
        emidetid,
        billno,
        paidby,
        paiddate: new Date().toISOString(),
      },
      {
        headers: commonHeaders,
      }
    );
    if (!data?.success) throw new Error(data?.message || "Failed to mark due paid");
  };

  // -------------------------
  // Load sequence
  // -------------------------
  const applyCustomerFromSelection = async () => {
    const selectedRows = location.state?.selectedRows || [];
    if (Array.isArray(selectedRows) && selectedRows.length > 0) {
      const first = selectedRows[0];
      const cid = String(first.customerId || first.customerid || "").trim();
      if (cid) {
        setFormData((p) => ({
          ...p,
          customerId: cid,
          customerName: first.customerName || "",
        }));
      }
    }
  };

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        await Promise.all([fetchCustomers(), fetchAssets()]);
        await applyCustomerFromSelection();
      } catch (e) {
        console.error(e);
        toast.error(e.message || "Failed to load page data");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const cid = String(formData.customerId || "").trim();
    if (!cid) return;

    (async () => {
      try {
        setLoading(true);
        const cd = await fetchCustomerDetails(cid);

        setFormData((p) => ({
          ...p,
          customerId: cid,
          customerName: cd?.fld_customername ?? p.customerName,
          leadSource: cd?.fld_leadsource ?? cd?.fld_source ?? "",
          productName: cd?.fld_Productname ?? "",
          salesPerson: cd?.fld_salesperson ?? cd?.fld_salespersonname ?? "",
          fullAddress: cd?.fld_fulladdress ?? cd?.fld_address ?? "",
          mobileNumber: cd?.fld_mobileno ?? cd?.fld_mobile ?? "",
          totalSqFeet: cd?.fld_extend ?? cd?.fld_sqfeet ?? "",
          location: cd?.fld_location ?? p.location ?? defaultLocation,
          address: cd?.fld_address ?? "",
          propertyInNameOf: cd?.fld_propertyname ?? "",
          dateOfAcquisition: cd?.fld_acquisitiondate
            ? String(cd.fld_acquisitiondate).slice(0, 10)
            : "",
          valueOfAsset: cd?.fld_PurchaseValue ?? cd?.fld_assetvalue ?? "",
          purchaseValue: cd?.fld_PurchaseValue ?? "",
          enquiryNumber: cd?.fld_enquirnum ?? "",
          assetId: cd?.fld_assetid ? String(cd.fld_assetid) : p.assetId,
          assetClass: cd?.fld_assetclass ?? "",
        }));

        if (cd?.fld_assetid) {
          const aid = String(cd.fld_assetid);
          const found = (assets || []).find((a) => String(a.fld_assetid) === aid);
          if (found) {
            setFormData((p) => ({
              ...p,
              assetId: aid,
              plotNoAssetCode: found.fld_assetcode,
            }));
          }
        }

        await fetchPaymentGrid(cid);
      } catch (e) {
        console.error(e);
        toast.error(e.message || "Failed to load customer data");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.customerId]);

  // -------------------------
  // Handlers
  // -------------------------
  const onCustomerChange = (cid) => {
    setFormData((p) => ({
      ...p,
      customerId: cid,
      customerName:
        customers.find((c) => String(c.fld_customerid) === String(cid))
          ?.fld_customername || "",
    }));
  };

  const onAssetChange = (assetId) => {
    const found = assets.find((a) => String(a.fld_assetid) === String(assetId));
    setFormData((p) => ({
      ...p,
      assetId,
      plotNoAssetCode: found?.fld_assetcode || "",
    }));
  };

  const openUpdateModalForRow = async (row) => {
    if (!row?.canUpdate) return;

    try {
      setModalLoading(true);
      setActiveRow(row);
      setShowUpdateModal(true);

      const det = await fetchDueDetails(row.emidetid);

      setUpdateData({
        date: det?.fld_date || "",
        customerIdName:
          det?.fld_customeridname || `${det?.fld_customerid || ""}`,
        dueAmount: det?.fld_dueamount || "",
        monthYear: det?.fld_monthyear || "",
        status: "Paid",
        amountType: "Cash",
      });
    } catch (e) {
      console.error(e);
      toast.error(e.message || "Failed to load due details");
      setShowUpdateModal(false);
      setActiveRow(null);
    } finally {
      setModalLoading(false);
    }
  };

  const closeUpdateModal = () => {
    setShowUpdateModal(false);
    setActiveRow(null);
  };

  const saveUpdateModal = async () => {
    if (!activeRow?.emidetid) return;

    try {
      setModalLoading(true);
      const billno = await fetchEmiBillNo();

      await markDuePaid({
        emidetid: activeRow.emidetid,
        billno,
        paidby: loginUserId,
      });

      toast.success("EMI Due updated as Paid!");

      if (formData.customerId) await fetchPaymentGrid(formData.customerId);

      closeUpdateModal();
    } catch (e) {
      console.error(e);
      toast.error(e.message || "Failed to update EMI due");
    } finally {
      setModalLoading(false);
    }
  };

  const onPrintRow = (row) => {
    navigate("/Sales/EmiPaymentBillPrint", {
      state: {
        customerData: formData,
        paymentData: row,
        paymentList: gridMapped,
      },
    });
  };

  // -------------------------
  // UI
  // -------------------------
  return (
    <SalesLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">EMI PAYMENT ENTRY</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(-1)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700"
                }`}
              >
                <FaArrowLeft />
                Back
              </button>
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                EMI Payment Entry
              </h1>
              {loading && (
                <span className={`text-sm ${isDark ? "text-gray-300" : "text-gray-500"}`}>
                  Loading...
                </span>
              )}
            </div>
          </div>

          <div className={`p-6 rounded-lg mb-6 ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
            <h3 className={`text-lg font-semibold mb-4 ${isDark ? "text-white" : "text-blue-900"}`}>
              EMI PAYMENT ENTRY
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Customer ID
                  </label>
                  <select
                    value={formData.customerId}
                    onChange={(e) => onCustomerChange(e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value="">Select</option>
                    {customers.map((c) => (
                      <option key={c.fld_customerid} value={c.fld_customerid}>
                        {c.fld_customerid}
                      </option>
                    ))}
                  </select>
                </div>

                {[
                  ["Customer Name", "customerName"],
                  ["Lead Source", "leadSource"],
                  ["Product Name", "productName"],
                  ["Sales Person", "salesPerson"],
                ].map(([label, key]) => (
                  <div key={key}>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                      {label}
                    </label>
                    <input
                      value={formData[key] || ""}
                      readOnly
                      className={`w-full px-4 py-2 border rounded-lg ${
                        isDark
                          ? "bg-gray-600 border-gray-600 text-gray-200"
                          : "bg-gray-100 border-gray-300 text-gray-800"
                      }`}
                    />
                  </div>
                ))}

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Full Address
                  </label>
                  <textarea
                    rows={3}
                    value={formData.fullAddress || ""}
                    readOnly
                    className={`w-full px-4 py-2 border rounded-lg resize-none ${
                      isDark
                        ? "bg-gray-600 border-gray-600 text-gray-200"
                        : "bg-gray-100 border-gray-300 text-gray-800"
                    }`}
                  />
                </div>

                {[
                  ["Mobile Number", "mobileNumber"],
                  ["Total Sq.Feet", "totalSqFeet"],
                ].map(([label, key]) => (
                  <div key={key}>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                      {label}
                    </label>
                    <input
                      value={formData[key] || ""}
                      readOnly
                      className={`w-full px-4 py-2 border rounded-lg ${
                        isDark
                          ? "bg-gray-600 border-gray-600 text-gray-200"
                          : "bg-gray-100 border-gray-300 text-gray-800"
                      }`}
                    />
                  </div>
                ))}
              </div>

              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Plot No / Asset Code
                  </label>
                  <select
                    value={formData.assetId}
                    onChange={(e) => onAssetChange(e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value="">Select</option>
                    {assets.map((a) => (
                      <option key={a.fld_assetid} value={a.fld_assetid}>
                        {a.fld_assetcode}
                      </option>
                    ))}
                  </select>
                </div>

                {[
                  ["Asset Class", "assetClass"],
                  ["Location", "location"],
                  ["Address", "address"],
                ].map(([label, key]) => (
                  <div key={key}>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                      {label}
                    </label>
                    <input
                      value={formData[key] || ""}
                      readOnly
                      className={`w-full px-4 py-2 border rounded-lg ${
                        isDark
                          ? "bg-gray-600 border-gray-600 text-gray-200"
                          : "bg-gray-100 border-gray-300 text-gray-800"
                      }`}
                    />
                  </div>
                ))}

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Property In the Name of
                  </label>
                  <textarea
                    rows={2}
                    value={formData.propertyInNameOf || ""}
                    readOnly
                    className={`w-full px-4 py-2 border rounded-lg resize-none ${
                      isDark
                        ? "bg-gray-600 border-gray-600 text-gray-200"
                        : "bg-gray-100 border-gray-300 text-gray-800"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Date of Acquisition
                  </label>
                  <input
                    value={formData.dateOfAcquisition || ""}
                    readOnly
                    className={`w-full px-4 py-2 border rounded-lg ${
                      isDark
                        ? "bg-gray-600 border-gray-600 text-gray-200"
                        : "bg-gray-100 border-gray-300 text-gray-800"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Value of Asset
                  </label>
                  <input
                    value={formData.valueOfAsset || ""}
                    readOnly
                    className={`w-full px-4 py-2 border rounded-lg ${
                      isDark
                        ? "bg-gray-600 border-gray-600 text-gray-200"
                        : "bg-gray-100 border-gray-300 text-gray-800"
                    }`}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className={`p-6 rounded-lg ${isDark ? "bg-gray-800/50" : "bg-white"}`}>
            <h3 className={`text-lg font-semibold mb-4 ${isDark ? "text-white" : "text-gray-900"}`}>
              Payment Records
            </h3>

            <div className="overflow-x-auto rounded-lg border shadow-lg">
              <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
                <thead className={`${buttonClass} text-white`}>
                  <tr>
                    <th className="px-3 py-3 text-left text-xs font-semibold">Sl No</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold">Date</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold">Billno</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold">Phase</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold">Payable Amount</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold">Paid Amount</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold">Balance Amount</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold">Over Due + Interest Amount</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold">Status</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold">Bill</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold">Action</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200">
                  {gridMapped.length === 0 ? (
                    <tr>
                      <td
                        colSpan={11}
                        className={`px-4 py-8 text-center ${isDark ? "text-gray-300" : "text-gray-600"}`}
                      >
                        No records found.
                      </td>
                    </tr>
                  ) : (
                    gridMapped.map((r, idx) => (
                      <tr
                        key={`${r.emitype}-${r.emidetid}-${idx}`}
                        className={`${
                          idx % 2 === 0
                            ? isDark
                              ? "bg-gray-800"
                              : "bg-white"
                            : isDark
                            ? "bg-gray-750"
                            : "bg-gray-50"
                        }`}
                      >
                        <td className={`px-3 py-3 text-xs ${isDark ? "text-gray-200" : "text-gray-900"}`}>{r.slNo}</td>
                        <td className={`px-3 py-3 text-xs ${isDark ? "text-gray-200" : "text-gray-900"}`}>{r.date}</td>
                        <td className={`px-3 py-3 text-xs ${isDark ? "text-gray-200" : "text-gray-900"}`}>{r.billno}</td>
                        <td className={`px-3 py-3 text-xs ${isDark ? "text-gray-200" : "text-gray-900"}`}>{r.phase}</td>

                        <td className={`px-3 py-3 text-xs font-semibold ${isDark ? "text-gray-100" : "text-gray-900"}`}>
                          {formatAmount(r.payableAmount)}
                        </td>
                        <td className={`px-3 py-3 text-xs ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                          {formatAmount(r.paidAmount)}
                        </td>
                        <td className={`px-3 py-3 text-xs font-semibold ${isDark ? "text-gray-100" : "text-gray-900"}`}>
                          {formatAmount(r.balanceAmount)}
                        </td>

                        <td className={`px-3 py-3 text-xs ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                          {r.overDuePlusInterest ? String(r.overDuePlusInterest) : ""}
                        </td>

                        <td className="px-3 py-3 text-xs">
                          <span
                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              isActiveStatus(r.status)
                                ? "bg-blue-100 text-blue-800"
                                : "bg-green-100 text-green-800"
                            }`}
                          >
                            {r.status}
                          </span>
                        </td>

                        <td className="px-3 py-3 text-xs">
                          <button
                            onClick={() => onPrintRow(r)}
                            className="inline-flex items-center gap-2 text-blue-700 hover:underline"
                            title="Print Receipt"
                          >
                            <FaPrint />
                            Print
                          </button>
                        </td>

                        <td className="px-3 py-3 text-xs">
                          {r.canUpdate ? (
                            <button
                              onClick={() => openUpdateModalForRow(r)}
                              className={`px-3 py-2 rounded-md text-white font-semibold ${buttonClass} ${hoverClass}`}
                            >
                              Update
                            </button>
                          ) : (
                            <span className={`${isDark ? "text-gray-400" : "text-gray-500"}`}>-</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className={`mt-3 text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              * Update button appears only for <b>Active</b> EMI Dues.
            </div>
          </div>

          {showUpdateModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
              <div
                className={`w-full max-w-xl rounded-xl border shadow-2xl ${
                  isDark
                    ? "bg-gray-900 border-gray-700 text-white"
                    : "bg-sky-200 border-sky-300 text-gray-900"
                }`}
              >
                <div className="flex items-center justify-between px-6 py-4 border-b border-black/10">
                  <h3 className="text-lg font-bold">EMI PAYMENT DETAILS UPDATE</h3>
                  <button
                    onClick={closeUpdateModal}
                    className={`p-2 rounded-md ${isDark ? "hover:bg-gray-800" : "hover:bg-sky-300"}`}
                    title="Close"
                  >
                    <FaTimes />
                  </button>
                </div>

                <div className="px-6 py-5 space-y-4">
                  {modalLoading ? (
                    <div className="py-6 text-center">Loading...</div>
                  ) : (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold mb-1">Date</label>
                          <input
                            value={updateData.date || ""}
                            readOnly
                            className={`w-full px-3 py-2 rounded border ${
                              isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-300"
                            }`}
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-semibold mb-1">
                            Customer ID / Name <span className="text-red-600">*</span>
                          </label>
                          <input
                            value={updateData.customerIdName || ""}
                            readOnly
                            className={`w-full px-3 py-2 rounded border ${
                              isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-300"
                            }`}
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-semibold mb-1">
                            Due Amount <span className="text-red-600">*</span>
                          </label>
                          <input
                            value={updateData.dueAmount || ""}
                            readOnly
                            className={`w-full px-3 py-2 rounded border ${
                              isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-300"
                            }`}
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-semibold mb-1">
                            Due Month - Year <span className="text-red-600">*</span>
                          </label>
                          <input
                            value={updateData.monthYear || ""}
                            readOnly
                            className={`w-full px-3 py-2 rounded border ${
                              isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-300"
                            }`}
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-semibold mb-1">
                            Payment Status <span className="text-red-600">*</span>
                          </label>
                          <input
                            value="Paid"
                            readOnly
                            className={`w-full px-3 py-2 rounded border ${
                              isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-300"
                            }`}
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-semibold mb-1">Amount Type (Cash/Bank)</label>
                          <div className="flex items-center gap-4 pt-2">
                            <label className="flex items-center gap-2">
                              <input
                                type="radio"
                                name="amountType"
                                value="Cash"
                                checked={updateData.amountType === "Cash"}
                                onChange={(e) =>
                                  setUpdateData((p) => ({ ...p, amountType: e.target.value }))
                                }
                              />
                              Cash
                            </label>
                            <label className="flex items-center gap-2">
                              <input
                                type="radio"
                                name="amountType"
                                value="Bank"
                                checked={updateData.amountType === "Bank"}
                                onChange={(e) =>
                                  setUpdateData((p) => ({ ...p, amountType: e.target.value }))
                                }
                              />
                              Bank
                            </label>
                          </div>
                        </div>
                      </div>

                      <div className="flex justify-center pt-2">
                        <button
                          onClick={saveUpdateModal}
                          disabled={modalLoading}
                          className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold text-white shadow-lg transition ${
                            modalLoading
                              ? "opacity-60 cursor-not-allowed"
                              : `${buttonClass} ${hoverClass}`
                          }`}
                        >
                          <FaSave />
                          Save
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </SalesLayoutWrapper>
  );
};

export default EMIPaymentEntry;